import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import moment from 'moment';
import { GlobalToken, Popover, Tabs } from 'antd';
import Icon from '@ant-design/icons';
import { some } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { TFunction } from 'i18next';

import { useStores } from '@/hooks/use-stores';
import { AlertLevels, ComponentTierName, AlertTypes, StationTier, AlertRuleTypes } from '@/constants/enums';
import { checkTierPermission } from '@/utils/util-functions';
import IconConfigure from '@/assets/icon-configure.svg?react';
import IconNotification from '@/assets/icon-notification.svg?react';

import Alert, { AlertEntity } from './Alert';
import AlertsList from './AlertsList';
import ArchiveOutlineIcon from '../common/icons/ArchiveOutlineIcon';
import SeverityIcon from '../common/icons/SeverityIcon';

moment.updateLocale('en', {
  calendar: {
    lastDay: '[Yesterday,] h:mma',
    sameDay: '[Today,] h:mma',
    nextDay: 'MMM D[,] Y[,] h:mma',
    lastWeek: 'MMM D[,] Y[,] h:mma',
    nextWeek: 'MMM D[,] Y[,] h:mma',
    sameElse: 'MMM D[,] Y[,] h:mma',
  },
});

// Define proper interfaces for components
interface AllTabLabelProps {
  openCount: number;
  closedCount: number;
  hasUnread: boolean;
  t: TFunction;
}

interface ArchivedTabLabelProps {
  t: TFunction;
}

// Standard function components instead of memo for simplicity
function AllTabLabel({ openCount, closedCount, hasUnread, t }: AllTabLabelProps) {
  return (
    <>
      <div className="tab-label">{`${t('All')} ${openCount} (${closedCount})`}</div>
      {hasUnread && <IconNotification className="icon-notification" />}
    </>
  );
}

function ArchivedTabLabel({ t }: ArchivedTabLabelProps) {
  return (
    <div className="tab-label">
      <Popover placement="bottom" content={<span>{t('Auto & manually archived alerts')}</span>}>
        <ArchiveOutlineIcon className="archive-icon" />
      </Popover>
    </div>
  );
}

interface AlertsProps {
  alerts: AlertEntity[];
}

const Alerts = ({ alerts }: AlertsProps) => {
  const location = useLocation();
  const params = useParams();
  const [openAlertId, setOpenAlertId] = useState<string>();
  const { alertStore, stationStore } = useStores();
  const { t } = useTranslation();
  const shouldRestrictAlerts = checkTierPermission(ComponentTierName.PaymentPlan, stationStore);

  const [activeKey, setActiveKey] = useState(alertStore.defaultKey);
  const activeAlerts = alertStore.filterActiveAlerts(alerts);
  const archivedAlerts = alertStore.filterArchivedAlerts(alerts);
  const shutdownAlerts = alertStore.filterShutdownAlerts(activeAlerts);
  const warningAlerts = alertStore.filterWarningAlerts(activeAlerts);
  const serviceAlerts = alertStore.filterServiceAlerts(activeAlerts);
  const isMonitorStation = stationStore.currentStation.stationTier === StationTier.Monitor;
  const ruleType = isMonitorStation ? `/${AlertRuleTypes.MANUFACTURER.toLowerCase()}` : '';
  const openAlerts = alertStore.filterOpenAlerts(activeAlerts);
  const closedAlerts = alertStore.filterClosedAlerts(activeAlerts);
  const openShutdownAlerts = alertStore.filterOpenAlerts(shutdownAlerts);
  const openWarningAlerts = alertStore.filterOpenAlerts(warningAlerts);
  const openServiceAlerts = alertStore.filterOpenAlerts(serviceAlerts);
  const closedShutdownAlerts = alertStore.filterClosedAlerts(shutdownAlerts);
  const closedWarningAlerts = alertStore.filterClosedAlerts(warningAlerts);
  const closedServiceAlerts = alertStore.filterClosedAlerts(serviceAlerts);

  // Create alert elements
  const activeAlertsElem = activeAlerts.map(alert => (
    <Alert
      key={alert.id}
      alert={alert}
      toggleOpen={toggleOpen}
      setOpenAlertId={setOpenAlertId}
      isOpen={alert.id === openAlertId}
      isListWithSelection={!shouldRestrictAlerts}
    />
  ));

  const shutdownAlertsElem = shutdownAlerts.map(alert => (
    <Alert
      key={alert.id}
      alert={alert}
      toggleOpen={toggleOpen}
      setOpenAlertId={setOpenAlertId}
      isOpen={alert.id === openAlertId}
      isListWithSelection={!shouldRestrictAlerts}
    />
  ));

  const warningAlertElem = warningAlerts.map(alert => (
    <Alert
      key={alert.id}
      alert={alert}
      toggleOpen={toggleOpen}
      setOpenAlertId={setOpenAlertId}
      isOpen={alert.id === openAlertId}
      isListWithSelection={!shouldRestrictAlerts}
    />
  ));

  const serviceAlertElem = serviceAlerts.map(alert => (
    <Alert
      key={alert.id}
      alert={alert}
      toggleOpen={toggleOpen}
      setOpenAlertId={setOpenAlertId}
      isOpen={alert.id === openAlertId}
      isListWithSelection={!shouldRestrictAlerts}
    />
  ));

  const archivedAlertElem = archivedAlerts.map(alert => (
    <Alert
      key={alert.id}
      alert={alert}
      toggleOpen={toggleOpen}
      setOpenAlertId={setOpenAlertId}
      isOpen={alert.id === openAlertId}
    />
  ));

  useEffect(() => {
    setActiveKey(alertStore.defaultKey);
  }, [location]);

  // sets all alerts as 'Read' in case a new alert is added
  // and 'all' tabs is already selected
  useEffect(() => {
    if (activeKey === AlertLevels.Shutdown) return;
    alertStore.setIsRead(activeKey);
  }, [alerts, activeKey]);

  const onChangeTab = key => {
    setActiveKey(key);
    alertStore.resetSelectedAlerts();
    alertStore.setIsRead(key);
  };

  const hasUnread = alerts => {
    return some(alerts, { isRead: false });
  };

  function toggleOpen(alert) {
    if (!alert) return;

    const isOpen = alert.id === openAlertId;
    isOpen ? setOpenAlertId(undefined) : setOpenAlertId(alert.id);
  }

  const commonAlertProps = { isLoading: alertStore.isLoading, openAlertId, setOpenAlertId };

  const tabItems = [
    {
      label: (
        <AllTabLabel
          openCount={openAlerts.length}
          closedCount={closedAlerts.length}
          hasUnread={hasUnread(activeAlerts)}
          t={t}
        />
      ),
      key: AlertTypes.Active,
      children: (
        <AlertsList
          {...commonAlertProps}
          alerts={activeAlertsElem}
          type={AlertTypes.Active}
          onChangeTab={onChangeTab}
        />
      ),
    },
    {
      label: (
        <div className="tab-label">
          <SeverityIcon severity={AlertLevels.Shutdown} />
          {`${openShutdownAlerts.length} (${closedShutdownAlerts.length})`}
        </div>
      ),
      key: AlertLevels.Shutdown,
      children: <AlertsList {...commonAlertProps} alerts={shutdownAlertsElem} />,
    },
    {
      label: (
        <div className="tab-label">
          <SeverityIcon severity={AlertLevels.Warning} />
          {`${openWarningAlerts.length} (${closedWarningAlerts.length})`}
        </div>
      ),
      key: AlertLevels.Warning,
      children: <AlertsList {...commonAlertProps} alerts={warningAlertElem} />,
    },
    {
      label: (
        <div className="tab-label">
          <SeverityIcon severity={AlertLevels.Service} />
          {`${openServiceAlerts.length} (${closedServiceAlerts.length})`}
        </div>
      ),
      key: AlertLevels.Service,
      children: <AlertsList {...commonAlertProps} alerts={serviceAlertElem} />,
    },
    {
      label: <ArchivedTabLabel t={t} />,
      key: AlertTypes.Archived,
      children: <AlertsList {...commonAlertProps} alerts={archivedAlertElem} type={AlertTypes.Archived} />,
    },
  ];

  return (
    <Container $shouldBeDisabled={shouldRestrictAlerts}>
      <header>
        <span className="title-label">
          {t('ALERTS')} ({activeAlerts.length})
        </span>
        <Link to={`/home/station/${params.stationId}/rules${ruleType}`}>
          <Icon component={IconConfigure} style={{ fontSize: 24 }} />
        </Link>
      </header>

      <Tabs
        defaultActiveKey={activeKey}
        activeKey={activeKey}
        onChange={onChangeTab}
        animated={{
          inkBar: true,
          tabPane: false,
        }}
        items={tabItems}
      />
    </Container>
  );
};

interface ContainerProps {
  $shouldBeDisabled: boolean;
  theme?: { token: GlobalToken }; // Make theme optional since it's injected by styled-components
}

const Container = styled.div<ContainerProps>`
  height: 100%;
  display: flex;
  flex-direction: column;

  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 ${({ theme: { token } }) => token.paddingMD}px;

    ${({ $shouldBeDisabled }) => $shouldBeDisabled && 'pointer-events: none; cursor: default;'}
    .title-label {
      font-size: ${({ theme }) => theme?.token?.fontSizeLG}px;
      font-weight: ${({ theme }) => theme?.token?.fontWeightStrong};
      color: ${({ theme }) => theme?.token?.colorText};
    }
  }

  .ant-tabs {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    color: ${({ theme: { token } }) => token.colorText};

    .ant-tabs-nav {
      margin-right: ${({ theme: { token } }) => token.marginMD}px;
      margin-left: ${({ theme: { token } }) => token.marginMD}px;

      ${({ $shouldBeDisabled }) => $shouldBeDisabled && 'pointer-events: none; cursor: default;'}
      &:before {
        border-bottom-color: ${({ theme }) => theme?.token?.colorBgElevated};
      }
    }

    .ant-tabs-nav-wrap {
      .ant-tabs-nav-list {
        width: 100%;

        .ant-tabs-tab {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0;
          flex-grow: 1;
          min-width: 0;

          .ant-tabs-tab-btn {
            font-size: ${({ theme }) => theme?.token?.fontSizeSM}px;
            user-select: none;
          }

          .tab-label {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          svg {
            margin-right: ${({ theme: { token } }) => token.marginXXS}px;
          }

          .archive-icon {
            margin-right: 0;
          }
        }

        .ant-tabs-ink-bar {
          background-color: ${({ theme }) => theme?.token?.colorPrimary};
          height: 3px;
          border-radius: 5px 5px 0px 0px;
        }
      }
    }

    .no-more-alerts-label {
      display: flex;
      align-items: center;
      justify-content: center;
      padding-top: ${({ theme: { token } }) => token.paddingMD}px;
      padding-bottom: ${({ theme: { token } }) => token.paddingSM}px;
      color: ${({ theme: { token } }) => token.colorBorder};
    }

    .no-alerts-to-show-label {
      text-align: center;
      color: ${({ theme }) => theme?.token?.colorBorder};
    }

    .no-archived-alerts-label {
      text-align: center;
      color: ${({ theme }) => theme?.token?.colorBorder};
    }
  }

  // fixes Alerts flicker bug

  .ant-tabs-nav-operations {
    display: none !important;
  }

  .ant-tabs-content-holder {
    display: flex;
    flex-direction: column;
    overflow-y: scroll;

    .ant-tabs-content {
      flex-grow: 1;
      flex-shrink: 0;
      height: 0px;

      ${({ $shouldBeDisabled }) => $shouldBeDisabled && 'pointer-events: none; cursor: default;'}
      .ant-tabs-tabpane {
        padding-right: ${({ theme: { token } }) => token.paddingXXS}px;
        padding-left: ${({ theme: { token } }) => token.paddingXS}px;
      }
    }
  }

  .icon-notification {
    position: absolute;
    top: 0px;
    right: -7px;
  }
`;

export default observer(Alerts);
